import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api/axios';
import { toast } from 'sonner';
import { 
  LogOut, Users, FileText, TrendingUp, MessageSquare,
  CreditCard, Menu, X, Check, XCircle, Clock, ChevronDown
} from 'lucide-react';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [applications, setApplications] = useState([]);
  const [users, setUsers] = useState([]);
  const [leads, setLeads] = useState([]);
  const [creditCards, setCreditCards] = useState([]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedApp, setSelectedApp] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, appsRes, usersRes, leadsRes, cardsRes] = await Promise.all([
        api.get('/admin/stats'),
        api.get('/admin/applications'),
        api.get('/admin/users'),
        api.get('/admin/leads'),
        api.get('/credit-cards')
      ]);
      
      setStats(statsRes.data);
      setApplications(appsRes.data);
      setUsers(usersRes.data);
      setLeads(leadsRes.data);
      setCreditCards(cardsRes.data);
    } catch (error) {
      toast.error('Failed to fetch admin data');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const updateApplicationStatus = async (appId, newStatus, notes = null, approvedAmount = null, interestRate = null) => {
    try {
      await api.patch(`/admin/applications/${appId}/status`, {
        new_status: newStatus,
        admin_notes: notes,
        approved_amount: approvedAmount,
        interest_rate: interestRate
      });
      toast.success('Application status updated');
      fetchData();
      setSelectedApp(null);
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const updateLeadStatus = async (leadId, newStatus, notes = null) => {
    try {
      await api.patch(`/admin/leads/${leadId}/status`, {
        new_status: newStatus,
        notes
      });
      toast.success('Lead status updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update lead');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'bg-gray-500',
      submitted: 'bg-blue-500',
      under_review: 'bg-yellow-500',
      documents_required: 'bg-orange-500',
      approved: 'bg-green-500',
      rejected: 'bg-red-500',
      disbursed: 'bg-purple-500',
      new: 'bg-blue-500',
      contacted: 'bg-yellow-500',
      qualified: 'bg-green-500',
      converted: 'bg-purple-500',
      lost: 'bg-red-500'
    };
    return colors[status] || 'bg-gray-500';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#354F37]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <nav className="bg-[#354F37] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <Link to="/" className="text-2xl font-bold">OJOVEST Admin</Link>
            </div>
            
            <div className="hidden md:flex items-center space-x-4">
              <span>Admin: {user?.full_name}</span>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 hover:text-white transition"
              >
                <LogOut size={20} />
                Logout
              </button>
            </div>

            <button 
              className="md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden bg-blue-700 px-4 py-4">
            <button onClick={handleLogout} className="flex items-center gap-2">
              <LogOut size={20} />
              Logout
            </button>
          </div>
        )}
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid md:grid-cols-4 gap-6 mb-8" data-testid="admin-stats">
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <Users className="text-[#354F37]" size={32} />
              <span className="text-3xl font-bold text-gray-800">{stats?.total_users || 0}</span>
            </div>
            <p className="text-gray-600">Total Users</p>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <FileText className="text-green-600" size={32} />
              <span className="text-3xl font-bold text-gray-800">{stats?.total_applications || 0}</span>
            </div>
            <p className="text-gray-600">Applications</p>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <Clock className="text-yellow-600" size={32} />
              <span className="text-3xl font-bold text-gray-800">{stats?.pending_applications || 0}</span>
            </div>
            <p className="text-gray-600">Pending</p>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <MessageSquare className="text-purple-600" size={32} />
              <span className="text-3xl font-bold text-gray-800">{stats?.new_leads || 0}</span>
            </div>
            <p className="text-gray-600">New Leads</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-xl shadow-sm mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex overflow-x-auto">
              <button
                onClick={() => setActiveTab('overview')}
                className={`px-6 py-4 font-semibold border-b-2 transition whitespace-nowrap ${
                  activeTab === 'overview'
                    ? 'border-[#354F37] text-[#354F37]'
                    : 'border-transparent text-gray-600 hover:text-[#354F37]'
                }`}
              >
                Overview
              </button>
              <button
                onClick={() => setActiveTab('applications')}
                className={`px-6 py-4 font-semibold border-b-2 transition whitespace-nowrap ${
                  activeTab === 'applications'
                    ? 'border-[#354F37] text-[#354F37]'
                    : 'border-transparent text-gray-600 hover:text-[#354F37]'
                }`}
              >
                Applications ({applications.length})
              </button>
              <button
                onClick={() => setActiveTab('leads')}
                className={`px-6 py-4 font-semibold border-b-2 transition whitespace-nowrap ${
                  activeTab === 'leads'
                    ? 'border-[#354F37] text-[#354F37]'
                    : 'border-transparent text-gray-600 hover:text-[#354F37]'
                }`}
              >
                Leads ({leads.length})
              </button>
              <button
                onClick={() => setActiveTab('users')}
                className={`px-6 py-4 font-semibold border-b-2 transition whitespace-nowrap ${
                  activeTab === 'users'
                    ? 'border-[#354F37] text-[#354F37]'
                    : 'border-transparent text-gray-600 hover:text-[#354F37]'
                }`}
              >
                Users ({users.length})
              </button>
              <button
                onClick={() => setActiveTab('credit-cards')}
                className={`px-6 py-4 font-semibold border-b-2 transition whitespace-nowrap ${
                  activeTab === 'credit-cards'
                    ? 'border-[#354F37] text-[#354F37]'
                    : 'border-transparent text-gray-600 hover:text-[#354F37]'
                }`}
              >
                Credit Cards
              </button>
            </nav>
          </div>

          <div className="p-6">
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-6">Admin Dashboard Overview</h2>
                
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="font-semibold text-lg mb-4">Recent Applications</h3>
                    <div className="space-y-3">
                      {applications.slice(0, 5).map((app) => (
                        <div key={app.id} className="border border-gray-200 rounded-lg p-4">
                          <div className="flex justify-between items-start">
                            <div>
                              <p className="font-semibold">{app.loan_type.replace('_', ' ').toUpperCase()}</p>
                              <p className="text-sm text-gray-600">₹{app.loan_amount.toLocaleString('en-IN')}</p>
                            </div>
                            <span className={`px-3 py-1 rounded-full text-white text-xs ${getStatusColor(app.status)}`}>
                              {app.status.replace('_', ' ')}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="font-semibold text-lg mb-4">Recent Leads</h3>
                    <div className="space-y-3">
                      {leads.slice(0, 5).map((lead) => (
                        <div key={lead.id} className="border border-gray-200 rounded-lg p-4">
                          <div className="flex justify-between items-start">
                            <div>
                              <p className="font-semibold">{lead.name}</p>
                              <p className="text-sm text-gray-600">{lead.phone}</p>
                            </div>
                            <span className={`px-3 py-1 rounded-full text-white text-xs ${getStatusColor(lead.status)}`}>
                              {lead.status}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Applications Tab */}
            {activeTab === 'applications' && (
              <div data-testid="admin-applications-tab">
                <h2 className="text-2xl font-bold text-gray-800 mb-6">Loan Applications Management</h2>
                
                <div className="space-y-4">
                  {applications.map((app) => (
                    <div key={app.id} className="border border-gray-200 rounded-lg p-6">
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <h3 className="text-xl font-semibold">{app.loan_type.replace('_', ' ').toUpperCase()} Loan</h3>
                          <p className="text-sm text-gray-600">ID: {app.id.substring(0, 8)}</p>
                        </div>
                        <span className={`px-4 py-2 rounded-full text-white text-sm font-semibold ${getStatusColor(app.status)}`}>
                          {app.status.replace('_', ' ')}
                        </span>
                      </div>

                      <div className="grid md:grid-cols-3 gap-4 mb-4">
                        <div>
                          <p className="text-sm text-gray-600">Requested Amount</p>
                          <p className="text-lg font-semibold">₹{app.loan_amount.toLocaleString('en-IN')}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Monthly Income</p>
                          <p className="text-lg font-semibold">₹{app.monthly_income.toLocaleString('en-IN')}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Employment</p>
                          <p className="text-lg font-semibold">{app.employment_type}</p>
                        </div>
                      </div>

                      <div className="mb-4">
                        <p className="text-sm text-gray-600 mb-1">Purpose:</p>
                        <p className="text-gray-800">{app.loan_purpose}</p>
                      </div>

                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={() => updateApplicationStatus(app.id, 'under_review')}
                          className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 text-sm"
                          data-testid="status-under-review-btn"
                        >
                          Under Review
                        </button>
                        <button
                          onClick={() => updateApplicationStatus(app.id, 'approved', 'Application approved', app.loan_amount, 10.5)}
                          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm"
                          data-testid="status-approved-btn"
                        >
                          Approve
                        </button>
                        <button
                          onClick={() => updateApplicationStatus(app.id, 'rejected', 'Application rejected')}
                          className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm"
                          data-testid="status-rejected-btn"
                        >
                          Reject
                        </button>
                        <button
                          onClick={() => updateApplicationStatus(app.id, 'documents_required', 'Additional documents required')}
                          className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 text-sm"
                        >
                          Request Documents
                        </button>
                      </div>
                    </div>
                  ))}

                  {applications.length === 0 && (
                    <p className="text-center text-gray-500 py-8">No applications yet</p>
                  )}
                </div>
              </div>
            )}

            {/* Leads Tab */}
            {activeTab === 'leads' && (
              <div data-testid="admin-leads-tab">
                <h2 className="text-2xl font-bold text-gray-800 mb-6">Leads Management</h2>
                
                <div className="space-y-4">
                  {leads.map((lead) => (
                    <div key={lead.id} className="border border-gray-200 rounded-lg p-6">
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <h3 className="text-xl font-semibold">{lead.name}</h3>
                          <p className="text-gray-600">{lead.phone}</p>
                          {lead.email && <p className="text-gray-600">{lead.email}</p>}
                        </div>
                        <span className={`px-4 py-2 rounded-full text-white text-sm font-semibold ${getStatusColor(lead.status)}`}>
                          {lead.status}
                        </span>
                      </div>

                      <div className="mb-4">
                        <p className="text-sm text-gray-600">Source: <span className="font-semibold">{lead.source}</span></p>
                        <p className="text-sm text-gray-600">Interest: <span className="font-semibold">{lead.interest}</span></p>
                        {lead.message && <p className="text-sm text-gray-800 mt-2">{lead.message}</p>}
                        {lead.notes && (
                          <div className="mt-2 bg-blue-50 p-3 rounded">
                            <p className="text-sm text-blue-800"><strong>Notes:</strong> {lead.notes}</p>
                          </div>
                        )}
                      </div>

                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={() => updateLeadStatus(lead.id, 'contacted', 'Lead contacted')}
                          className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 text-sm"
                        >
                          Mark Contacted
                        </button>
                        <button
                          onClick={() => updateLeadStatus(lead.id, 'qualified', 'Lead qualified')}
                          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm"
                        >
                          Qualified
                        </button>
                        <button
                          onClick={() => updateLeadStatus(lead.id, 'converted', 'Lead converted to customer')}
                          className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm"
                        >
                          Converted
                        </button>
                        <button
                          onClick={() => updateLeadStatus(lead.id, 'lost', 'Lead lost')}
                          className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm"
                        >
                          Lost
                        </button>
                      </div>
                    </div>
                  ))}

                  {leads.length === 0 && (
                    <p className="text-center text-gray-500 py-8">No leads yet</p>
                  )}
                </div>
              </div>
            )}

            {/* Users Tab */}
            {activeTab === 'users' && (
              <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-6">Users Management</h2>
                
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">KYC</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Joined</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {users.map((user) => (
                        <tr key={user.id}>
                          <td className="px-6 py-4 whitespace-nowrap">{user.full_name}</td>
                          <td className="px-6 py-4 whitespace-nowrap">{user.email}</td>
                          <td className="px-6 py-4 whitespace-nowrap">{user.phone}</td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 text-xs rounded-full ${user.kyc_verified ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                              {user.kyc_verified ? 'Verified' : 'Pending'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {new Date(user.created_at).toLocaleDateString('en-IN')}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {users.length === 0 && (
                    <p className="text-center text-gray-500 py-8">No users yet</p>
                  )}
                </div>
              </div>
            )}

            {/* Credit Cards Tab */}
            {activeTab === 'credit-cards' && (
              <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-6">Credit Cards Management</h2>
                
                <div className="grid md:grid-cols-2 gap-6">
                  {creditCards.map((card) => (
                    <div key={card.id} className="border border-gray-200 rounded-lg p-6">
                      <h3 className="text-xl font-semibold mb-2">{card.name}</h3>
                      <p className="text-gray-600 mb-4">{card.bank} • {card.card_type}</p>
                      
                      <div className="grid grid-cols-2 gap-4 mb-4">
                        <div>
                          <p className="text-sm text-gray-600">Annual Fee</p>
                          <p className="font-semibold">₹{card.annual_fee.toLocaleString('en-IN')}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Rewards</p>
                          <p className="font-semibold">{card.rewards_rate}</p>
                        </div>
                      </div>

                      <div className="flex items-center gap-4 text-sm">
                        {card.lounge_access && (
                          <span className="flex items-center gap-1 text-green-600">
                            <Check size={16} /> Lounge
                          </span>
                        )}
                        {card.concierge_service && (
                          <span className="flex items-center gap-1 text-green-600">
                            <Check size={16} /> Concierge
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
